/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Flag, CheckCircle, XCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";

interface QuestionReport {
  id: number;
  userId: number;
  gameType: string;
  questionId: number;
  reason: string;
  details: string | null;
  status: string;
  reviewedById: number | null;
  reviewedAt: string | null;
  createdAt: string;
  user: { id: number; name: string; email: string };
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  reviewed: "bg-green-100 text-green-700",
  dismissed: "bg-gray-100 text-gray-500",
};

const REASON_LABELS: Record<string, string> = {
  incorrect: "Incorrect Answer",
  offensive: "Offensive Content",
  duplicate: "Duplicate Question",
  other: "Other",
};

const GAME_LABELS: Record<string, string> = {
  mcq: "MCQ",
  "true-or-false": "True or False",
  "who-am-i": "Who Am I",
  "guess-player": "Guess The Player",
  "career-path": "Career Path",
  "mystery-club": "Mystery Club",
  "higher-or-lower": "Higher or Lower",
  tictactoe: "Tic-Tac-Toe",
};

export default function ReportsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [gameFilter, setGameFilter] = useState<string>("all");

  const { data: reports = [], isLoading } = useQuery<QuestionReport[]>({
    queryKey: ["question-reports", statusFilter, gameFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (gameFilter !== "all") params.set("gameType", gameFilter);
      const qs = params.toString();
      return apiClient.get(
        `${API_ENDPOINTS.QUIZ.REPORTS_LIST}${qs ? `?${qs}` : ""}`
      ) as any;
    },
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "reviewed" | "dismissed" }) =>
      apiClient.post(API_ENDPOINTS.QUIZ.REPORT_REVIEW(id), { status }) as any,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["question-reports"] });
      toast.success("Report updated");
    },
    onError: () => toast.error("Failed to update report"),
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flag className="h-6 w-6" />
            <h1 className="text-2xl font-bold">Reported Questions</h1>
            {reports.length > 0 && (
              <Badge variant="secondary">{reports.length}</Badge>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Game Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Games</SelectItem>
                {Object.entries(GAME_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : reports.length === 0 ? (
          <p className="text-muted-foreground">No reports found.</p>
        ) : (
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Game</th>
                  <th className="px-4 py-3 text-left font-medium">Q ID</th>
                  <th className="px-4 py-3 text-left font-medium">Reason</th>
                  <th className="px-4 py-3 text-left font-medium">Details</th>
                  <th className="px-4 py-3 text-left font-medium">Reporter</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Badge variant="outline">
                        {GAME_LABELS[r.gameType] || r.gameType}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono">{r.questionId}</td>
                    <td className="px-4 py-3">
                      {REASON_LABELS[r.reason] || r.reason}
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate">
                      {r.details || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs">{r.user.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={STATUS_COLORS[r.status] || ""}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {r.status === "pending" && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Mark reviewed"
                            onClick={() =>
                              reviewMutation.mutate({ id: r.id, status: "reviewed" })
                            }
                          >
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Dismiss"
                            onClick={() =>
                              reviewMutation.mutate({ id: r.id, status: "dismissed" })
                            }
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
